import React from "react";
import { Button, Space, Tag, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  MinusOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  LabReadiness,
  LAB_STATE_COLOR,
  LAB_STATE_LABEL,
  LabState,
} from "./labReadiness";

const LAB_STATE_ICON: Record<LabState, React.ReactNode> = {
  not_required: <MinusOutlined />,
  ready: <CheckCircleOutlined />,
  provisioning: <SyncOutlined spin />,
  pending: <ClockCircleOutlined />,
  unknown: <ExclamationCircleOutlined />,
};

type Props = {
  readiness: LabReadiness;
  checking: boolean;
  /* Set when this backend has no readiness endpoint, so the tag is running on the
     list payload alone. Worth saying out loud: the state is still true, it just
     moves at the pace of the table refresh rather than live. */
  liveUnavailable?: boolean;
  onRefresh: () => void;
};

const checkLabel = "Check lab status now";
/* Says what the click will actually do on a backend with no readiness endpoint,
   rather than promising a live check it cannot make. */
const refreshLabel = "Refresh lab status from the latest session data";

/* The lab column: what the candidate's infrastructure is doing, so an examiner can
   see whether Verify will be accepted before clicking it. */
const LabStatusTag: React.FC<Props> = ({
  readiness,
  checking,
  liveUnavailable,
  onRefresh,
}) => {
  const tag = (
    <Tag
      color={LAB_STATE_COLOR[readiness.state]}
      icon={LAB_STATE_ICON[readiness.state]}
      style={{ marginInlineEnd: 0 }}
    >
      {LAB_STATE_LABEL[readiness.state]}
    </Tag>
  );

  /* A paper with no lab has no infrastructure to report on — showing a re-check
     button there would only invite clicks that can never change anything. */
  if (readiness.state === "not_required") {
    return <Tooltip title={readiness.message}>{tag}</Tooltip>;
  }

  const detail = (
    <>
      <div>{readiness.message}</div>
      {readiness.provisioning_state ? (
        <div style={{ marginTop: 4, opacity: 0.75 }}>
          Lab service reports: {readiness.provisioning_state}
        </div>
      ) : null}
      <div style={{ marginTop: 4, opacity: 0.75 }}>
        {liveUnavailable
          ? "Live lab checks are unavailable on this server — showing the status from the last table refresh."
          : readiness.checked_at
            ? `Last checked ${moment.unix(readiness.checked_at).format("hh:mm:ssA")}`
            : "Not checked live yet"}
      </div>
    </>
  );

  return (
    <Space size={4}>
      <Tooltip title={detail}>{tag}</Tooltip>
      <Tooltip title={liveUnavailable ? refreshLabel : checkLabel}>
        <Button
          type="text"
          size="small"
          aria-label={liveUnavailable ? refreshLabel : checkLabel}
          icon={<ReloadOutlined spin={checking} />}
          onClick={onRefresh}
        />
      </Tooltip>
    </Space>
  );
};

export default LabStatusTag;
